/* global gtag */
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import _isBoolean from 'lodash/isBoolean';
import _isEqual from 'lodash/isEqual';
import { ConstructedNavigationBar as NavigationBar } from 'styles/blocks/navigationBar';
import {
  setPractice,
  setTreatmentCategory,
  getFirstQuestion,
  setPatient, getPreviousQuestion, getNextQuestion, getFirstPrelimQuestion,
} from 'redux/actions/consultation';
import { getLastQuestion, setPaused } from 'redux/actions/consultation/question';
import {
  // restartSession,
  backToTreatmentCategoryListing,
  cancelProgress, closeCancelPageWarning,
} from 'redux/actions/session';
import { setForceNhsLoginMode, setIsClinicalReviewMode } from 'redux/actions/flags';
import { Consultation } from 'components/consultation';
import { ColumnDiv } from 'styles/elements/div';
import { consultationSelector } from 'redux/selectors/consultation';
import { CancelProgressModal } from 'components/consultation/modal/modal';
import { STRINGS } from '../../config';
import { throttled } from '../../redux/throttle';

export function ETriageContainer({ treatmentCategoryCodeNameAsProps, isClinicalReviewModeAsProps, practiceAsProps,
  product, patientAsProps, showNavBar, backNav, currentQuestionId }) {
  // Use this container as a higher order component
  const mapState = useCallback(state => consultationSelector(state), []);
  const {
    treatmentCategory,
    isClinicalReviewMode,
    isForwardNavigation,
    apiVersion,
    practice,
    patient,
    sessionId,
    question,
    validationErrors,
    responseType,
    isCancelProgressModalOpen,
    previous,
    forceNhsLoginModeAsProps,
    paused,
  } = useMappedState(mapState);
  // Create actions
  const dispatch = useDispatch();
  const isEndOfPrelimSection = currentQuestionId === STRINGS.RESPONSE_TYPE.QUESTION_END_OF_PRELIMINARY;
  const firstClinical = useRef(false);

  useEffect(() => {
    if (!_isEqual(patientAsProps, patient)) {
      dispatch(setPatient(patientAsProps));
    }
  }, [dispatch, patientAsProps, patient]);

  // Any time the question changes, attempt to send a GA pageView event. This relies on the global ga variable being set by the main webapp
  useEffect(() => {
    if (typeof gtag !== 'undefined') {
      let url = `/virtual/consultation/qpp/${treatmentCategory || ''}/${question.questionId || ''}`;
      if (validationErrors && validationErrors.length > 0) {
        url += '/VALIDATION_ERROR';
      }
      gtag('event', 'virtual_page_view', {
        event_category: url,
        event_label: responseType,
        value: 1,
      });
    }
  }, [responseType, treatmentCategory, question, validationErrors]);

  useEffect(() => {
    if ((!practice.practiceId || !practice.odsCode) && practiceAsProps && practiceAsProps.practiceId && practiceAsProps.odsCode) {
      dispatch(setPractice(practiceAsProps));
    }
    if (treatmentCategoryCodeNameAsProps && treatmentCategoryCodeNameAsProps !== treatmentCategory) {
      // set flag to show we are retrieving first clinical question to prevent handleBrowserNavigationOnLoad re-calling API
      firstClinical.current = true;
      dispatch(setTreatmentCategory({ codeName: treatmentCategoryCodeNameAsProps }));
      dispatch(getFirstQuestion());
    }
  }, [dispatch, practiceAsProps, treatmentCategoryCodeNameAsProps, practice, treatmentCategory]);

  useEffect(() => {
    if (_isBoolean(forceNhsLoginModeAsProps)) {
      dispatch(setForceNhsLoginMode(forceNhsLoginModeAsProps));
    }
    if (_isBoolean(isClinicalReviewModeAsProps)) {
      dispatch(setIsClinicalReviewMode(isClinicalReviewModeAsProps));
    }
  }, [dispatch, forceNhsLoginModeAsProps, isClinicalReviewModeAsProps]);

  useEffect(() => {
    if (practice.practiceId && practice.odsCode && !sessionId) {
      // no session id so get the first preliminary question
      firstClinical.current = true;
      dispatch(getFirstPrelimQuestion());
    }
  }, [dispatch, practice, sessionId]);

  useEffect(() => {
    if (paused) {
      // if paused=true then we are about to exit React and return to webflow so ensure state changes are flushed to the session first
      throttled.flush();
    }
  }, [paused, dispatch]);

  const handleBrowserNavigationOnLoad = () => {
    // eTriage on-load syncs view to current position on QPP template. This is determined both by whether we entered React from a
    // forward or back click (backNav) but also by whether we previously left React in a forward or back direction (isForwardNavigation)
    // (as we may have changed direction within a webflow section)
    if (backNav) {
      if (sessionId) {
        if (isForwardNavigation || isEndOfPrelimSection) {
          // left React in forward direction, returned via back click, or we are between prelim and clinical so get the previous QPP question
          dispatch({ type: STRINGS.CONSULTATION_REDUCER.SYNC_TO_API_CURRENT_QUESTION, payload: currentQuestionId });
          dispatch(getPreviousQuestion());
        } else {
          // left React in backward direction, entered next (ie previous) React Q in backward direction so get the current ("last") QPP question
          dispatch(getLastQuestion());
        }
        dispatch(setPaused(false));
      }
    } else {
      // eslint-disable-next-line no-lonely-if
      if (sessionId) {
        dispatch(setPaused(false));
        // if effect has already fired to get the first clinical question then no need to re-call the API here
        if (!firstClinical.current) {
          if (isForwardNavigation || !previous) {
            // left React in forward direction, entered next React Q in forward direction OR we went back beyond the first prelim Q
            // so echo the current Q (ie last question returned from the API)
            if (backNav === undefined) {
              // ET-551: if backNav is undefined but firstClinical is false then we have re-entered the same clinical template that we backed out of
              dispatch(getFirstQuestion());
            } else {
              dispatch(getLastQuestion());
            }
          } else {
            // we left the last React Q in a backward direction and are now going forward again, so get the NEXT question
            dispatch({ type: STRINGS.CONSULTATION_REDUCER.SYNC_TO_API_CURRENT_QUESTION, payload: currentQuestionId });
            dispatch(getNextQuestion());
          }
        }
      }
    }
    firstClinical.current = false;
    // flush state to browser
    throttled.flush();
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    handleBrowserNavigationOnLoad();
  }, []);

  const consultationBody = () => {
    return <Consultation isClinicalReviewMode={isClinicalReviewMode} treatmentCategoryCodeNameAsProps={treatmentCategoryCodeNameAsProps} product={product} />;
  };

  return (
    <ColumnDiv modifier={['flexFill']}>
      {showNavBar && <NavigationBar onButtonClick={() => dispatch(backToTreatmentCategoryListing())} isClinicalReviewMode={isClinicalReviewMode} apiVersion={apiVersion} />}
      {consultationBody()}
      <CancelProgressModal onSelectConfirm={() => cancelProgress()} onSelectCancel={() => dispatch(closeCancelPageWarning())} isOpen={isCancelProgressModalOpen} />
    </ColumnDiv>
  );
}
